// ペンの色・太さの選好を localStorage に保存する。
// 色は任意の hex 文字列（"#RRGGBB"）として扱い、組み込みパレットと利用者が追加した
// カスタム色（最大 CUSTOM_COLOR_LIMIT 件）の組み合わせから選ぶ。

const COLOR_KEY = "refinement-canvas:pen:color";
const THICKNESS_KEY = "refinement-canvas:pen:thickness";
const CUSTOM_COLORS_KEY = "refinement-canvas:pen:custom-colors";

// 色は hex 文字列で扱う。型としては string と等価だが、意味合いの明示のため別名にしている。
export type PenColor = string;
export type PenThickness = "thin" | "medium" | "thick";

const VALID_THICKNESS: readonly PenThickness[] = ["thin", "medium", "thick"];

// 組み込みパレット。添付要望に近い色合いで構成しつつ、色覚配慮で十分な明度差を確保する。
// 先頭の色がデフォルト（保存値が無効・未保存のときの初期値）。
export const PEN_PRESET_COLORS: readonly PenColor[] = [
  "#1a1a2e",
  "#9b59b6",
  "#2da55a",
  "#d22b2b",
  "#e91e63",
  "#1e63d4",
  "#16a085",
  "#0d47a1",
  "#5e35b1",
  "#ef6c00",
  "#c2185b",
  "#7b1fa2",
  "#388e3c",
  "#0097a7",
  "#558b2f",
  "#fbc02d",
];

const DEFAULT_COLOR: PenColor = PEN_PRESET_COLORS[0] as PenColor;
const DEFAULT_THICKNESS: PenThickness = "medium";

/** ローカル保持できるカスタム色の上限。多くなりすぎて UI が崩れないよう抑える。 */
export const CUSTOM_COLOR_LIMIT = 12;

// v0.2.31 で PenColor を enum から hex 文字列へ変更した。旧バージョンの localStorage に
// 保存された "black" / "red" / "blue" を、新形式の hex 値へ読み替えるための辞書。
// 既存利用者の選択色が初回起動時に既定色（黒）へ戻らないようにする。
const LEGACY_PEN_COLOR_HEX: Record<string, PenColor> = {
  black: "#1a1a2e",
  red: "#d22b2b",
  blue: "#1e63d4",
};

const HEX_COLOR_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/** 与えられた文字列が "#RGB" または "#RRGGBB" 形式の hex 色かを判定する。 */
export function isValidHexColor(value: unknown): value is string {
  return typeof value === "string" && HEX_COLOR_PATTERN.test(value);
}

/** "#RGB" を "#RRGGBB" に正規化し、両形式でも比較用に小文字統一する。 */
export function normalizeHexColor(value: string): string {
  const lowered = value.trim().toLowerCase();
  if (lowered.length === 4) {
    const r = lowered[1] ?? "0";
    const g = lowered[2] ?? "0";
    const b = lowered[3] ?? "0";
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return lowered;
}

export function loadPenColor(): PenColor {
  try {
    const value = window.localStorage.getItem(COLOR_KEY);
    if (isValidHexColor(value)) {
      return normalizeHexColor(value);
    }
    // 旧 enum 形式（"black" / "red" / "blue"）が残っている場合は、対応する hex 値へ
    // 透過的に読み替える。明示的な保存上書きはせず、savePenColor が次回呼ばれた時に
    // hex 形式へ書き換わるのに任せる（旧バージョンに戻したときも壊さないため）。
    const legacy = value ? LEGACY_PEN_COLOR_HEX[value] : undefined;
    if (legacy) return legacy;
    return DEFAULT_COLOR;
  } catch {
    return DEFAULT_COLOR;
  }
}

export function savePenColor(color: PenColor): void {
  if (!isValidHexColor(color)) return;
  try {
    window.localStorage.setItem(COLOR_KEY, normalizeHexColor(color));
  } catch {
    // ignore（プライベートブラウジング等）
  }
}

/** 追加保存されたカスタム色一覧を読み込む。壊れた値は読み飛ばす。 */
export function loadPenCustomColors(): readonly PenColor[] {
  try {
    const raw = window.localStorage.getItem(CUSTOM_COLORS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const result: PenColor[] = [];
    for (const value of parsed) {
      if (isValidHexColor(value)) {
        const normalized = normalizeHexColor(value);
        if (!result.includes(normalized)) result.push(normalized);
      }
      if (result.length >= CUSTOM_COLOR_LIMIT) break;
    }
    return result;
  } catch {
    return [];
  }
}

/**
 * カスタム色を 1 件追加する。
 * - 組み込みパレットに含まれる色は重複としてカスタム側へは追加しない。
 * - 既にカスタム色に存在する場合は追加せず、現在のリストを返す。
 * - 上限 {@link CUSTOM_COLOR_LIMIT} に達している場合は古い方を 1 件捨てる（FIFO）。
 *
 * 返り値は更新後のカスタム色一覧。
 */
export function addPenCustomColor(color: PenColor): readonly PenColor[] {
  if (!isValidHexColor(color)) return loadPenCustomColors();
  const normalized = normalizeHexColor(color);
  if (PEN_PRESET_COLORS.includes(normalized)) return loadPenCustomColors();
  const current = loadPenCustomColors();
  if (current.includes(normalized)) return current;
  const next = [...current, normalized];
  while (next.length > CUSTOM_COLOR_LIMIT) next.shift();
  try {
    window.localStorage.setItem(CUSTOM_COLORS_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
  return next;
}

export function loadPenThickness(): PenThickness {
  try {
    const value = window.localStorage.getItem(THICKNESS_KEY);
    return VALID_THICKNESS.find((c) => c === value) ?? DEFAULT_THICKNESS;
  } catch {
    return DEFAULT_THICKNESS;
  }
}

export function savePenThickness(thickness: PenThickness): void {
  try {
    window.localStorage.setItem(THICKNESS_KEY, thickness);
  } catch {
    // ignore
  }
}

export const PEN_THICKNESS_PIXELS: Record<PenThickness, number> = {
  thin: 2,
  medium: 4,
  thick: 8,
};
